import { useLocation } from 'react-router';

import { RouteModule } from '#/horizon/components/RouteModule';
import { getServerRouterContext } from '#/horizon/router-contexts/server-router-context';
import type { LoaderDataWithOverrideLocale } from '#/horizon/utils/getMatchesOverrideLocale';
import type { LayoutHandleElements } from '#/layouts/Layout/LayoutHandleProps';
import { cmsApiServerFetchDocument } from '#/modules/cms/api/cmsApiFetchDocument';
import { CmsPagePage } from '#/modules/cms/documents/Page/CmsPage.page';
import { getCmsPageLayoutHandleProps } from '#/modules/cms/documents/Page/getCmsPageLayoutHandleProps';
import { CmsPostPage } from '#/modules/cms/documents/Post/CmsPost.page';
import { getCmsPostLayoutHandleProps } from '#/modules/cms/documents/Post/getCmsPostLayoutHandleProps';
import { getCmsSearchLandingLayoutHandleProps } from '#/modules/cms/helpers/getCmsSearchLandingLayoutHandleProps';
import { cmsSplatPath } from '#/modules/cms/routing/cms.paths';
import { getPostSearchPageLayoutHandleProps } from '#/modules/postSearch/pages/PostSearch/getPostSearchPageLayoutHandleProps';
import { postSearchPathReference } from '#/modules/postSearch/routing/postSearch.paths';
import { SearchRefinementsSwitch } from '#/modules/search/components/Refinements/SearchRefinementsSwitch';
import { searchGetPathSwitch } from '#/modules/search/routing/helpers/searchGetPathSwitch';
import { DynamicNotFoundPage } from '#/pages/NotFound/DynamicNotFoundPage';
import type { RoutePathLoaderArgs } from '#/routing/classes/types/RoutePathLoaderArgs';
import type { RouteProps } from '#/routing/types/RouteProps';
import { idealistNotFound } from '#/routing/utils/idealistNotFound';
import { idealistRedirect } from '#/routing/utils/idealistRedirect';
import { dynamic } from '#/utils/dynamic';
import { imageUrlForOgMeta } from '#/utils/filestack';

import type { CmsSplatLoaderData } from './CmsSplatLoaderData';
import { getCmsContentOgImage } from './getCmsContentOgImage';
import { idealistOrganizationSchemaMarkup } from './idealistOrganizationSchemaMarkup';

const DynamicPostSearchPage = dynamic(
  () =>
    import('#/modules/postSearch/pages/PostSearch/PostSearch.page').then(
      (m) => ({
        default: m.PostSearchPage,
      }),
    ),
  { strategy: 'always' },
);

const DynamicSearchLandingPage = dynamic(
  () =>
    import('#/modules/search/pages/Landing/SearchLanding.page').then((m) => ({
      default: m.SearchLandingPage,
    })),
  { strategy: 'always' },
);

export const handle = {
  searchRefinements: <SearchRefinementsSwitch />,
} satisfies LayoutHandleElements;

export async function loader({
  url,
  request,
  context,
  params: { ['*']: slug },
}: RoutePathLoaderArgs<typeof cmsSplatPath>) {
  const { serverContext } = getServerRouterContext(context);

  const searchParams = cmsSplatPath.searchParamsFromUrl(url);

  try {
    const { data: content, ssrLandingPageResults } =
      await cmsApiServerFetchDocument({
        request,
        context,
        forwardSurrogate: true,
      })({
        slug,
        preview: searchParams.preview === 'true',
        lang: serverContext.locale,
      });

    if (content.type === 'CMS_REDIRECT') {
      return idealistRedirect({ fromUrl: url, context, to: content.url });
    }

    if (content.type === 'SEARCH_LANDING_PAGE') {
      return {
        type: 'SEARCH_LANDING_PAGE',
        content,
        results: ssrLandingPageResults,
        handle: getCmsSearchLandingLayoutHandleProps({ content }),
        overrideLocale: content.locale,
        now: serverContext.now,
      } satisfies CmsSplatLoaderData & LoaderDataWithOverrideLocale;
    }

    if (typeof searchParams['pq'] !== 'undefined' && content.subsite.homePage) {
      return {
        type: 'POST_SEARCH',
        content,
        handle: getPostSearchPageLayoutHandleProps({ content }),
        overrideLocale: content.locale,
      } satisfies CmsSplatLoaderData & LoaderDataWithOverrideLocale;
    }

    if (content.heroSearch?.onlyResults) {
      const redirectPath = searchGetPathSwitch({
        q: typeof searchParams.q === 'string' ? searchParams.q : undefined,
        locale: serverContext.locale,
        filtersByName: content.heroSearch.defaultSearchFilters,
        embedSearchWidgetId: null,
      });

      return idealistRedirect({ fromUrl: url, context, to: redirectPath });
    }

    if (content.onlySearchResults && content.subsite?.homePage) {
      const redirectPath = postSearchPathReference.with({
        pq: typeof searchParams.pq === 'string' ? searchParams.pq : '',
        slug: content.url,
      });

      return idealistRedirect({ fromUrl: url, context, to: redirectPath });
    }

    if (content.type === 'PAGE') {
      return {
        type: 'PAGE',
        content,
        handle: getCmsPageLayoutHandleProps({ content }),
        overrideLocale: content.locale,
      } satisfies CmsSplatLoaderData & LoaderDataWithOverrideLocale;
    }

    // content.type === 'POST'
    return {
      type: 'POST',
      content,
      handle: getCmsPostLayoutHandleProps({ content }),
      overrideLocale: content.locale,
    } satisfies CmsSplatLoaderData & LoaderDataWithOverrideLocale;
  } catch {
    return idealistNotFound(null);
  }
}

export default function CmsSplatRoute({
  loaderData,
}: RouteProps<typeof loader>) {
  const location = useLocation();

  switch (loaderData?.type) {
    case undefined:
      return <DynamicNotFoundPage />;
    case 'PAGE': {
      const image = getCmsContentOgImage(loaderData.content);

      return (
        <RouteModule
          title={loaderData.content.headTitle ?? loaderData.content.title}
          ogImage={
            image
              ? {
                  url: imageUrlForOgMeta({ data: image }),
                  alt: image.altText ?? null,
                }
              : null
          }
          description={loaderData.content.metaDescription}
          robotsNoIndex={!loaderData.content.visibleToRobots}
          linkRels={
            loaderData.content.isPublished
              ? {
                  rssFeed:
                    loaderData.content.subsite.homePage?.id ===
                    loaderData.content.id
                      ? {
                          title: `${loaderData.content.headTitle ?? loaderData.content.title} Feed`,
                          href: `${loaderData.content.url}/feed.rss`,
                        }
                      : null,
                  translations: [
                    loaderData.content,
                    ...loaderData.content.translations,
                  ].map((translation) => ({
                    url: translation.url,
                    locale: translation.locale,
                  })),
                  canonical: loaderData.content.url,
                }
              : null
          }
          schemaMarkups={
            location.pathname === '/en'
              ? [idealistOrganizationSchemaMarkup]
              : undefined
          }
        >
          <CmsPagePage content={loaderData.content} />
        </RouteModule>
      );
    }
    case 'POST': {
      const image = getCmsContentOgImage(loaderData.content);

      return (
        <RouteModule
          title={loaderData.content.headTitle ?? loaderData.content.title}
          ogImage={
            image
              ? {
                  url: imageUrlForOgMeta({ data: image }),
                  alt: image.altText ?? null,
                }
              : null
          }
          description={loaderData.content.metaDescription}
          robotsNoIndex={!loaderData.content.visibleToRobots}
          article={{
            firstPublished: loaderData.content.articleMeta.firstPublished,
            lastModified: loaderData.content.articleMeta.lastModified,
            author: loaderData.content.author?.name ?? null,
            tags: loaderData.content.tags.map((tag) => tag.tag),
          }}
          linkRels={
            loaderData.content.isPublished
              ? {
                  rssFeed:
                    loaderData.content.subsite.homePage?.id ===
                    loaderData.content.id
                      ? {
                          title: `${loaderData.content.headTitle ?? loaderData.content.title} Feed`,
                          href: `${loaderData.content.url}/feed.rss`,
                        }
                      : null,
                  translations: [
                    loaderData.content,
                    ...loaderData.content.translations,
                  ].map((translation) => ({
                    url: translation.url,
                    locale: translation.locale,
                  })),
                  canonical: loaderData.content.url,
                }
              : null
          }
          schemaMarkups={[loaderData.content.schemaMarkup]}
        >
          <CmsPostPage content={loaderData.content} />
        </RouteModule>
      );
    }
    case 'SEARCH_LANDING_PAGE':
      return (
        <RouteModule
          title={
            loaderData.content.location?.name
              ? `${loaderData.content.name}, ${loaderData.content.location.name}`
              : loaderData.content.name
          }
          description={loaderData.content.description}
          linkRels={{
            translations: loaderData.content.translations.map(
              (translation) => ({
                url: translation.url,
                locale: translation.locale,
              }),
            ),
            canonical: location.pathname,
          }}
        >
          <DynamicSearchLandingPage
            content={loaderData.content}
            now={loaderData.now}
          />
        </RouteModule>
      );
    case 'POST_SEARCH':
      return (
        <RouteModule
          title={loaderData.content.headTitle ?? loaderData.content.title}
          description={loaderData.content.metaDescription}
        >
          <DynamicPostSearchPage content={loaderData.content} />
        </RouteModule>
      );
  }
}
